import React from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Landmark, X } from 'lucide-react';
import useEscapeKey from '../../hooks/useEscapeKey';
import { fetchWithAuth } from '../../utils/api';
import { useInstitutionImport } from './useInstitutionImport';
import { useSupportedInstitutionCount } from './useSupportedInstitutionCount';
import InstitutionImportDropZone from './InstitutionImportDropZone';
import InstitutionImportPreview from './InstitutionImportPreview';
import InstitutionImportUnrecognised from './InstitutionImportUnrecognised';
import InstitutionImportCountryList from './InstitutionImportCountryList';
import { buildSuccessToast } from './successToast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// The "Import from your institution" modal — Box 3 of the Add Investments tab.
// Replaces the earlier template-CSV uploader with a smarter, warmer flow that:
//   1. Reads the User's own institution file (any shape).
//   2. Either previews what it found, or honestly says "we don't have a reader yet"
//      and offers two options: enter manually, or ask the team to build a reader.
const InstitutionImportModal = ({
  isOpen, onClose, targetLabel = 'Portfolio Investments',
  onEnterManually, onImportSuccess,
}) => {
  const hook = useInstitutionImport(onClose);
  const counts = useSupportedInstitutionCount();
  useEscapeKey(hook.handleClose, isOpen);

  if (!isOpen) return null;

  const handleEnterManually = () => { hook.handleClose(); onEnterManually?.(); };

  const handleConfirmMatched = async ({ institution_id, investment_type, rows }) => {
    hook.setConfirmSaving(true);
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/institution-import/commit`, {
        method: 'POST',
        body: JSON.stringify({ institution_id, investment_type, rows }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.ok === false) {
        toast.error(body?.detail || 'We could not save those holdings — please try again.');
        return;
      }
      // Guard against the silent-loss case: server said ok:true but saved
      // zero rows. Surface the errors instead of the misleading success toast.
      if (body.created_count === 0) {
        const firstErr = body.errors?.[0]?.error || 'no rows were saved';
        toast.error(`We couldn't save those holdings — ${firstErr}. Please check the rows and try again.`);
        return;
      }
      const message = buildSuccessToast(body, targetLabel);
      onImportSuccess?.(message);
      if (body.error_count > 0) {
        toast.warning(`${body.error_count} row${body.error_count === 1 ? '' : 's'} couldn't be saved. Please check and re-add if needed.`);
      }
      hook.handleClose();
    } finally { hook.setConfirmSaving(false); }
  };

  const renderBody = () => {
    if (hook.stage === 'unmatched' || hook.stage === 'requested') {
      return (
        <InstitutionImportUnrecognised
          unmatched={hook.unmatched} requestSaving={hook.requestSaving}
          stage={hook.stage}
          onEditName={hook.updateEditableName}
          onEnterManually={handleEnterManually}
          onRequestReader={hook.handleRequestReader}
          onBack={hook.resetAll}
        />
      );
    }
    if (hook.stage === 'matched') {
      return (
        <InstitutionImportPreview
          preview={hook.preview}
          targetLabel={targetLabel}
          saving={hook.confirmSaving}
          onConfirm={handleConfirmMatched}
          onBack={hook.resetAll}
        />
      );
    }
    return (
      <InstitutionImportDropZone
        files={hook.files} file={hook.file} stage={hook.stage}
        fileInputRef={hook.fileInputRef}
        onFileSelected={hook.handleFileSelected}
      />
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
         data-testid="institution-import-modal">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <CardHeader className="border-b bg-slate-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Landmark className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <CardTitle className="text-lg" data-testid="institution-import-title">
                  Import records from your institution
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Upload your bank or asset manager&rsquo;s own CSV, PDF or screenshot &mdash; no reformatting.
                </p>
              </div>
            </div>
            <button
              type="button" onClick={hook.handleClose}
              className="p-1 hover:bg-slate-200 rounded"
              data-testid="institution-import-close"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {counts !== null && hook.stage === 'idle' && (
            <div className="mb-4 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600"
                 data-testid="institution-reader-counter">
              MyInvestFolio can extract investment details from files uploaded from{' '}
              <span className="font-semibold text-emerald-700">
                {counts.globalReadable} {counts.globalReadable === 1 ? 'institution' : 'institutions'}
              </span>
              {counts.globalCountries > 1 && (
                <> across{' '}
                  <span className="font-semibold text-slate-800">
                    {counts.globalCountries} countries
                  </span>
                </>
              )}
              {counts.readable > 0 && counts.readable !== counts.globalReadable && (
                <> — including{' '}
                  <span className="font-semibold text-slate-800">
                    {counts.readable} in your country
                  </span>
                </>
              )}
              . The list grows every three months as more members share theirs.
            </div>
          )}
          {counts !== null && hook.stage === 'idle' && (
            <InstitutionImportCountryList
              allInstitutions={counts.allInstitutions}
              userCountryIso={counts.userCountryIso}
            />
          )}
          {renderBody()}
        </CardContent>
      </Card>
    </div>
  );
};

export default InstitutionImportModal;
